import { Suspense, lazy, useEffect, useRef } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, useNavigationType } from "react-router-dom";
import { Toaster } from "sonner";

const Home = lazy(() => import("@/pages/Home"));
const Apply = lazy(() => import("@/pages/Apply"));
const Success = lazy(() => import("@/pages/Success"));
const Admin = lazy(() => import("@/pages/Admin"));
const Schedule = lazy(() => import("@/pages/Schedule"));
const ProductPage = lazy(() => import("@/pages/ProductPage"));
const Track = lazy(() => import("@/pages/Track"));
const AmbientChatter = lazy(() => import("@/components/overlay/AmbientChatter"));

/* Scroll-management for router transitions.
   Behavior:
   • PUSH (forward nav — clicked a link):  scroll to top of new page
     AND remember the scroll Y of the page you just left, keyed by the
     browser-history idx of that entry.
   • POP (browser Back/Forward):  restore the previously saved scroll
     position for the destination entry — lands you exactly where you
     were before leaving. Falls back to top if no memory.

   Scroll-lock: while `window.__emisonScrollLocked` is true (set by the
   vault-unlock animation which programmatically scrolls to the bottom
   before navigating), the passive scroll listener stops overwriting the
   saved scroll position for the current key. This preserves the user's
   ORIGINAL position so Back navigation restores correctly. */
function ScrollToTop() {
  const { pathname, key } = useLocation();
  const navType = useNavigationType(); // "PUSH" | "REPLACE" | "POP"
  const prevKeyRef = useRef(key);
  // Live-tracked scroll position — updated by the passive scroll listener AND
  // by capturing raw wheel/touchmove/scroll events. Reading this at PUSH time
  // is safer than window.scrollY because window.scrollY may already have been
  // clamped by the browser when the outgoing route's DOM shrinks.
  const liveYRef = useRef(0);

  useEffect(() => {
    if ("scrollRestoration" in window.history) {
      window.history.scrollRestoration = "manual";
    }
    // Save the scroll position of the location we are LEAVING before
    // React commits the new route render.
    // (prevKeyRef.current still points at the previous entry here.)
    const prevKey = prevKeyRef.current;

    if (navType === "POP") {
      // Back/Forward — restore saved scroll for this destination key.
      const saved = sessionStorage.getItem(`scroll:${key}`);
      const y = saved ? parseInt(saved, 10) : 0;
      console.log(`[ScrollToTop POP] Restoring scroll for key: ${key}, saved value: ${saved}, will scroll to: ${y}px`);
      // Multi-pass restoration — the 3D canvas remounts and pushes the layout
      // around, so we need to keep re-asserting the target position for a
      // moment. Uses rAF + timeouts to cover both immediate and deferred cases.
      const tryScroll = () => window.scrollTo(0, y);
      tryScroll();
      const raf = requestAnimationFrame(tryScroll);
      const t1 = setTimeout(tryScroll, 40);
      const t2 = setTimeout(tryScroll, 120);
      const t3 = setTimeout(tryScroll, 260);
      const t4 = setTimeout(tryScroll, 500);
      const t5 = setTimeout(tryScroll, 900);
      liveYRef.current = y;
      prevKeyRef.current = key;
      return () => {
        cancelAnimationFrame(raf);
        clearTimeout(t1); clearTimeout(t2); clearTimeout(t3);
        clearTimeout(t4); clearTimeout(t5);
      };
    }

    // PUSH or REPLACE — forward navigation.
    // Persist the leaving page's Y so a later back-nav can restore it.
    // Use liveYRef (the last scroll position we tracked) rather than
    // window.scrollY, because window.scrollY may have been clamped when
    // the outgoing route's DOM shrunk.  Also skip if the vault-unlock
    // animation pre-set a value with the scroll lock.
    if (!window.__emisonScrollLocked) {
      const savedY = liveYRef.current || window.scrollY || 0;
      console.log(`[ScrollToTop PUSH] Saving scroll position: ${savedY}px for prevKey: ${prevKey}`);
      sessionStorage.setItem(`scroll:${prevKey}`, String(savedY));
    } else {
      console.log(`[ScrollToTop PUSH] Lock is active, skipping save for prevKey: ${prevKey}`);
    }
    // Clear the lock now that navigation has happened.
    window.__emisonScrollLocked = false;
    console.log(`[ScrollToTop PUSH] Lock cleared`);

    window.scrollTo(0, 0);
    liveYRef.current = 0;
    const t = setTimeout(() => window.scrollTo(0, 0), 30);
    prevKeyRef.current = key;
    return () => clearTimeout(t);
  }, [pathname, key, navType]);

  // Continuously track scroll position via multiple listeners.  We update
  // liveYRef SYNCHRONOUSLY on every scroll event so it always holds the true
  // pre-navigation position — no rAF delay.  Persistence to sessionStorage is
  // still throttled via rAF to avoid storage thrashing.  This is critical:
  // when the outgoing route unmounts its long DOM (Home has 17 sections), the
  // browser clamps window.scrollY down.  If our save relied on window.scrollY
  // at that moment, we'd store the clamped value.  Reading liveYRef gives us
  // the position the user was actually at just before the click.
  useEffect(() => {
    let raf = 0;
    const onScroll = () => {
      // Don't update liveYRef when scroll is locked (vault animation in progress)
      // to preserve the origin scroll position for back-navigation.
      if (!window.__emisonScrollLocked) {
        liveYRef.current = window.scrollY || window.pageYOffset || 0;
      }
      if (raf) return;
      raf = requestAnimationFrame(() => {
        if (!window.__emisonScrollLocked) {
          sessionStorage.setItem(`scroll:${key}`, String(liveYRef.current));
        }
        raf = 0;
      });
    };
    // Also capture on wheel/touchmove so we never miss the very last position
    // when the user is actively scrolling and clicks a link.
    const onWheel = () => { 
      if (!window.__emisonScrollLocked) {
        liveYRef.current = window.scrollY || 0; 
      }
    };
    // Prime the ref with the current position at mount.
    liveYRef.current = window.scrollY || 0;
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("wheel", onWheel, { passive: true });
    window.addEventListener("touchmove", onWheel, { passive: true });
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("wheel", onWheel);
      window.removeEventListener("touchmove", onWheel);
      if (raf) cancelAnimationFrame(raf);
    };
  }, [key]);

  return null;
}

function Loader() {
  return (
    <div className="loader">
      <div className="text-center">
        <div className="ornament mb-3">Emison Capital</div>
        <div className="font-display text-3xl gold-text">Preparing your tour…</div>
        <div className="mt-6 gold-divider w-64 mx-auto" />
      </div>
    </div>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <ScrollToTop />
        <Toaster
          position="top-center"
          theme="light"
          closeButton
          toastOptions={{
            style: {
              background: "linear-gradient(180deg, #FAF6EC 0%, #F0E8D0 100%)",
              color: "#2A251F",
              border: "1px solid rgba(184,137,43,0.45)",
              boxShadow: "0 20px 60px -20px rgba(120,108,90,0.35), 0 2px 6px rgba(0,0,0,0.08)",
              fontFamily: "'Playfair Display', 'Times New Roman', serif",
              letterSpacing: "0.01em",
            },
            classNames: {
              title: "font-serif",
              description: "text-[13px]",
              closeButton:
                "!bg-transparent !border !border-[rgba(184,137,43,0.45)] !text-[#786c5a] hover:!text-[#B8892B]",
            },
          }}
        />
        <Suspense fallback={<Loader />}>
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/apply" element={<Apply />} />
            <Route path="/apply/success" element={<Success />} />
            <Route path="/admin" element={<Admin />} />
            <Route path="/schedule" element={<Schedule />} />
            <Route path="/products/:slug" element={<ProductPage />} />
            <Route path="/track" element={<Track />} />
          </Routes>
          {/* Global lounge audio — mounted here (outside <Routes>) so it
              persists across every page transition without remounting. */}
          <AmbientChatter />
        </Suspense>
      </BrowserRouter>
    </div>
  );
}

export default App;
